import { computed } from 'vue'
import { useSearchQueryStore } from '@/stores/searchQuery.js'
import { useFilterStore } from '@/stores/filter.js'
import { useCityStore } from '@/stores/city.js'
import { useCategoryStore } from '@/stores/category.js'
import { useSearchWithStore } from '@/composables/useSearchWithStore'
import { peopleList, peopleCapRange, roomSizeList, roomSizeRange } from '@/config/common.js'
import { useI18n } from '#imports'

// 已套用條件 chip:供桌機頂部條件列與手機篩選面板共用。
export interface AppliedChip {
  key: string
  label: string
  remove: () => void
}

interface RangeValue {
  min?: number
  max?: number
  index?: string
}

interface ListOption {
  id: number
  name: string
}

function isEmptyRange(range: RangeValue): boolean {
  return !range || Object.keys(range).length === 0
}

function findRangeName(
  range: RangeValue,
  rangeConfig: Record<number, RangeValue>,
  list: ListOption[],
): string {
  const matchedId = Object.keys(rangeConfig).find((key) => {
    const cfg = rangeConfig[Number(key)]
    return cfg?.min === range?.min && cfg?.max === range?.max
  })
  if (matchedId === undefined) {
    return ''
  }
  return list.find((option) => option.id === Number(matchedId))?.name ?? ''
}

interface RentTimeValue {
  rent_type?: number
  begin_time?: string
  end_time?: string
}

// 日期已套用 chip:2026-08-01 / 2026-08-01 09:00~11:00 / 2026-08-01（日租）。
// begin_time 為 'YYYY/MM/DD HH:mm:ss';未選具體時段時後端存 00:00:00~23:59:59,視為僅選日期。
function formatRentTime(rentTime: RentTimeValue): string {
  const beginTime = rentTime?.begin_time
  if (!beginTime) {
    return ''
  }
  const [datePart, beginClock] = beginTime.split(' ')
  const date = datePart.split('/').join('-')
  if (rentTime.rent_type === 1) {
    return `${date}（日租）`
  }
  const start = beginClock?.slice(0, 5) ?? ''
  const end = rentTime.end_time?.split(' ')[1]?.slice(0, 5) ?? ''
  const hasSpecificTime = start !== '' && end !== '' && !(start === '00:00' && end === '23:59')
  return hasSpecificTime ? `${date} ${start}~${end}` : date
}

// 價格已套用 chip 格式:$1,001 - $2,000 / $500 以下 / $10,001 以上
function formatPriceRange(range: RangeValue): string {
  const min = range.min ?? 0
  const max = range.max ?? 0
  if (max >= 999999) {
    return `$${min.toLocaleString()} 以上`
  }
  if (min <= 1) {
    return `$${max.toLocaleString()} 以下`
  }
  return `$${min.toLocaleString()} - $${max.toLocaleString()}`
}

export function useAppliedConditions() {
  const { t } = useI18n()
  const searchQueryStore = useSearchQueryStore()
  const filterStore = useFilterStore()
  const cityStore = useCityStore()
  const categoryStore = useCategoryStore()
  const { handleSearchWithStore } = useSearchWithStore()

  const usageCategoryId = import.meta.env.VITE_APP_BASE_USAGE_CATEGORY_ID
  const tagCategoryId = import.meta.env.VITE_APP_BASE_TAG_CATEGORY_ID

  function reSearch(): void {
    searchQueryStore.initGeoSearch()
    handleSearchWithStore()
  }

  const chips = computed<AppliedChip[]>(() => {
    const result: AppliedChip[] = []

    // 縣市
    const cityIds = searchQueryStore.getSearchCityId
    if (cityIds?.length) {
      const cityName = cityStore.getCityProperty(cityIds[0], 'city_name')
      if (cityName) {
        result.push({
          key: 'city',
          label: `${t('menu.location')}：${cityName}`,
          remove: () => {
            searchQueryStore.clearCitySelected()
            reSearch()
          },
        })
      }
    }

    // 用途
    const usageIds = searchQueryStore.getSearchCategoryIds?.[usageCategoryId]
    if (usageIds?.length) {
      const usageName = categoryStore.getUsageCategories.find((item: ListOption) => item.id === usageIds[0])?.name
      if (usageName) {
        result.push({
          key: 'usage',
          label: `${t('menu.usage')}：${usageName}`,
          remove: () => {
            searchQueryStore.clearCategoryObjectByKey(usageCategoryId)
            reSearch()
          },
        })
      }
    }

    // 人數
    const peopleRange = searchQueryStore.getPeopleCapRange
    if (!isEmptyRange(peopleRange)) {
      const name = findRangeName(peopleRange, peopleCapRange, peopleList)
      if (name) {
        result.push({
          key: 'people',
          label: `${t('menu.people')}：${name}`,
          remove: () => {
            searchQueryStore.setSelectedPeopleCapRange({})
            reSearch()
          },
        })
      }
    }

    // 價格
    const priceRangeValue = searchQueryStore.getPriceRange
    if (!isEmptyRange(priceRangeValue)) {
      result.push({
        key: 'price',
        label: `${t('menu.price')}：${formatPriceRange(priceRangeValue)}`,
        remove: () => {
          searchQueryStore.setSelectedPriceRange({})
          reSearch()
        },
      })
    }

    // 日期(AV P1 捕捉導向):僅顯示/移除,不影響搜尋結果(rent_time 已在 payload 剝除)
    const rentTimeLabel = formatRentTime(searchQueryStore.getRentTimeSelected)
    if (rentTimeLabel) {
      result.push({
        key: 'rentTime',
        label: `${t('menu.date')}：${rentTimeLabel}`,
        remove: () => {
          searchQueryStore.setRentTimeSelected({ rent_type: 0, begin_time: '', end_time: '' })
          reSearch()
        },
      })
    }

    // 坪數
    const roomSizeRangeValue = searchQueryStore.getRoomSizeRange
    if (!isEmptyRange(roomSizeRangeValue)) {
      const name = findRangeName(roomSizeRangeValue, roomSizeRange, roomSizeList)
      if (name) {
        result.push({
          key: 'roomSize',
          label: `${t('menu.room_size')}：${name}`,
          remove: () => {
            searchQueryStore.setSelectedRoomSizeRange({})
            reSearch()
          },
        })
      }
    }

    // 靠近捷運
    if (searchQueryStore.getNeedsToBeCloseToMrt) {
      result.push({
        key: 'mrt',
        label: t('filter.needs_to_be_close_to_mrt'),
        remove: () => {
          searchQueryStore.setNeedsToBeCloseToMrt(false)
          reSearch()
        },
      })
    }

    // 設備
    filterStore.getEquipmentList
      .filter((item: { selected?: boolean }) => item.selected)
      .forEach((item: { equipment_id: number; equipment_name: string }) => {
        result.push({
          key: `equip-${item.equipment_id}`,
          label: item.equipment_name,
          remove: () => {
            item.selected = false
            searchQueryStore.setEquipmentIds(filterStore.getSelectedEquipment)
            reSearch()
          },
        })
      })

    // 服務/特色
    filterStore.getServiceList
      .filter((item: { selected?: boolean }) => item.selected)
      .forEach((item: { service_id: number; service_name: string }) => {
        result.push({
          key: `service-${item.service_id}`,
          label: item.service_name,
          remove: () => {
            item.selected = false
            searchQueryStore.setServiceIds(filterStore.getSelectedService)
            reSearch()
          },
        })
      })

    // 風格
    filterStore.getCategoryTagList
      .filter((item: { selected?: boolean }) => item.selected)
      .forEach((item: { id: number; name: string }) => {
        result.push({
          key: `tag-${item.id}`,
          label: item.name,
          remove: () => {
            item.selected = false
            searchQueryStore.setSelectedCategoryObject({ [tagCategoryId]: filterStore.getSelectedCategoryTag })
            reSearch()
          },
        })
      })

    return result
  })

  function clearAll(): void {
    searchQueryStore.clearCitySelected()
    searchQueryStore.clearCategoryObjectByKey(usageCategoryId)
    searchQueryStore.clearCategoryObjectByKey(tagCategoryId)
    searchQueryStore.setSelectedPeopleCapRange({})
    searchQueryStore.setSelectedPriceRange({})
    searchQueryStore.setSelectedRoomSizeRange({})
    searchQueryStore.setRentTimeSelected({ rent_type: 0, begin_time: '', end_time: '' })
    searchQueryStore.setNeedsToBeCloseToMrt(false)
    filterStore.clearFilterSelected()
    searchQueryStore.setEquipmentIds([])
    searchQueryStore.setServiceIds([])
    reSearch()
  }

  return {
    chips,
    count: computed(() => chips.value.length),
    clearAll,
  }
}
